import { useState, type ReactNode } from 'react';
import {
  apiErrorText,
  useGetChronumPayrollOverviewQuery,
  useSyncChronumPayrollMutation,
  useEnqueuePayrollBatchToPaymentMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import { formatMillions } from '../financierFormat';
import { moscowTodayIso } from '../workingCapital/taxPeriods';
import {
  Container,
  HeaderRow,
  TitleBox,
  Title,
  Subtitle,
  HeaderActions,
  MonthPicker,
  MonthSelect,
  TabNav,
  TabButton,
  LoadingCard,
  ErrorCard,
  AlertBanner,
  NoDataCard,
  PeriodCaption,
} from './styled';
import { PayrollKpiGrid } from './PayrollKpiGrid';
import { PayrollBatchesTable } from './PayrollBatchesTable';
import { PayrollSitesTable } from './PayrollSitesTable';
import { monthLabel, monthStart, monthTitle, recentMonths, syncResultText } from './model';

export function PayrollView() {
  const { can_write: canWrite } = useAgentPermission('finance');
  // Текущий месяц — по Москве, как на сервере: от него месяц по умолчанию и «на смене
  // сейчас», а сервер по нему решает, писать ли численность из Хронума
  const currentMonth = monthStart(moscowTodayIso());
  // Месяц сводки обязателен: ведомости, объекты и итоги — только за него
  const [month, setMonth] = useState(currentMonth);
  // currentData — данные именно выбранного месяца: при смене месяца не показываем прежний
  const { currentData: data, isFetching, isError, refetch } = useGetChronumPayrollOverviewQuery({
    period_month: month,
  });
  const [syncChronum, { isLoading: isSyncing }] = useSyncChronumPayrollMutation();
  const [enqueueBatch, { isLoading: isEnqueuing }] = useEnqueuePayrollBatchToPaymentMutation();

  const [activeTab, setActiveTab] = useState<'batches' | 'sites'>('batches');
  const [banner, setBanner] = useState<{ text: string; tone: 'success' | 'warning' } | null>(null);
  const [enqueuingId, setEnqueuingId] = useState<string | null>(null);

  async function handleSync() {
    if (isSyncing) return;
    try {
      const res = await syncChronum({ period_month: month }).unwrap();
      setBanner(syncResultText(res, formatMillions));
    } catch (err) {
      setBanner({
        text: apiErrorText(err, 'Не удалось выполнить синхронизацию с ПК «Хронум»'),
        tone: 'warning',
      });
    }
  }

  async function handleEnqueue(batchId: string) {
    if (isEnqueuing) return;
    setEnqueuingId(batchId);
    try {
      await enqueueBatch({ batch_id: batchId }).unwrap();
      setBanner({
        text: 'Зарплатная ведомость передана в Фабрику платежей на акцепт Гендиректора.',
        tone: 'success',
      });
    } catch (err) {
      setBanner({
        text: apiErrorText(err, 'Не удалось передать зарплатную ведомость в Фабрику платежей'),
        tone: 'warning',
      });
    } finally {
      setEnqueuingId(null);
    }
  }

  const monthOptions = recentMonths(currentMonth, 13);
  const isCurrentMonth = month === currentMonth;

  let body: ReactNode;
  if (!data && isFetching) {
    body = <LoadingCard>Загрузка зарплатного шлюза ПК «Хронум» за {monthLabel(month)}…</LoadingCard>;
  } else if (isError || !data) {
    body = (
      <ErrorCard>
        <span>Не удалось загрузить данные зарплатного шлюза ПК «Хронум» за {monthLabel(month)}.</span>
        <Button $variant="secondary" $size="sm" onClick={() => refetch()}>
          Повторить
        </Button>
      </ErrorCard>
    );
  } else {
    const { totals, batches, site_accruals: sites } = data;
    body = (
      <>
        {/* KPI карточки: без строк из Хронума — объяснение вместо нулей */}
        {sites.length === 0 ? (
          <NoDataCard>
            Данных из Хронума за {monthLabel(data.period_month)} нет. Смены и начисления по
            объектам из Хронума в ОСНОВУ пока не загружаются.
          </NoDataCard>
        ) : (
          <>
            <PeriodCaption>
              Итоги за {monthLabel(data.period_month)} — по объектам этого месяца
            </PeriodCaption>
            <PayrollKpiGrid
              totals={totals}
              periodMonth={data.period_month}
              isCurrentMonth={isCurrentMonth}
            />
          </>
        )}

        {/* Переключение вкладок */}
        <TabNav>
          <TabButton
            $active={activeTab === 'batches'}
            onClick={() => setActiveTab('batches')}
          >
            Зарплатные ведомости к выплате ({batches.length})
          </TabButton>
          <TabButton
            $active={activeTab === 'sites'}
            onClick={() => setActiveTab('sites')}
          >
            Детализация по объектам строительства ({sites.length})
          </TabButton>
        </TabNav>

        {/* Вкладка 1: Ведомости к выплате */}
        {activeTab === 'batches' && (
          <PayrollBatchesTable
            batches={batches}
            periodMonth={data.period_month}
            onEnqueue={handleEnqueue}
            isEnqueuing={isEnqueuing}
            enqueuingId={enqueuingId}
            canWrite={Boolean(canWrite)}
          />
        )}

        {/* Вкладка 2: Детализация по строительным объектам */}
        {activeTab === 'sites' && (
          <>
            <PeriodCaption>
              Объекты за {monthLabel(data.period_month)}: смены и начисления этого месяца
            </PeriodCaption>
            <PayrollSitesTable
              sites={sites}
              periodMonth={data.period_month}
              isCurrentMonth={isCurrentMonth}
            />
          </>
        )}
      </>
    );
  }

  return (
    <Container>
      <HeaderRow>
        <TitleBox>
          <Title>Шлюз ПК «Хронум»: УРВ, табель и зарплатные ведомости</Title>
          <Subtitle>
            Биометрический учет рабочего времени, пообъектная сверка с рапортами прорабов и выплата ФОТ
          </Subtitle>
        </TitleBox>

        <HeaderActions>
          <MonthPicker>
            Месяц
            <MonthSelect value={month} onChange={(e) => setMonth(e.target.value)}>
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {monthTitle(m)}
                </option>
              ))}
            </MonthSelect>
          </MonthPicker>
          {canWrite && (
            <Button
              $variant="primary"
              $size="sm"
              onClick={handleSync}
              disabled={isSyncing || !data}
            >
              {isSyncing ? 'Синхронизация…' : `Синхронизировать за ${monthLabel(month)}`}
            </Button>
          )}
        </HeaderActions>
      </HeaderRow>

      {banner && (
        <AlertBanner $tone={banner.tone}>
          <span>{banner.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setBanner(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </AlertBanner>
      )}

      {body}
    </Container>
  );
}
